// #570: Termin aus einer E-Mail anlegen. Liest Datum/Uhrzeit aus dem Mailtext (ohne KI, nur Muster
// wie „am 12.11.2026 um 14:30 Uhr“ oder „Dienstag, 5. November, 10 Uhr“) und baut daraus die
// Vorbelegung fürs Terminformular. Alles bleibt im Formular änderbar.

const MONATE: Record<string, number> = {
  januar: 1, jan: 1, jänner: 1, februar: 2, feb: 2, märz: 3, maerz: 3, mär: 3, april: 4, apr: 4, mai: 5,
  juni: 6, jun: 6, juli: 7, jul: 7, august: 8, aug: 8, september: 9, sep: 9, sept: 9, oktober: 10, okt: 10,
  november: 11, nov: 11, dezember: 12, dez: 12,
};

// Ab hier beginnt der zitierte Verlauf – Daten darin („Am 12.10. um 9:14 schrieb …“) sind keine Termine.
// Outlook-Köpfe stehen im Klartext nicht immer am Zeilenanfang – daher meist ohne ^.
const ZITAT_BEGINN = [
  /^\s*>/m,
  /-{2,}\s*(Ursprüngliche Nachricht|Original Message|Weitergeleitete Nachricht|Forwarded message)/i,
  /Anfang der weitergeleiteten Nachricht/i,
  /^\s*(Von|From):\s.+$/im,
  /\b(Von|From):\s[^\n]{1,200}?\s(Gesendet|Sent|Datum|Date):\s/i,
  /^\s*(Gesendet|Sent):\s.+$/im,
  /\bAm [^\n]{4,80}? schrieb\b/i,
  /\bOn [^\n]{4,80}? wrote:?/i,
  /\bschrieb am\s/i,
  /Details der ursprünglichen Nachricht/i,
];

/** Eigener Text der Mail ohne zitierten Verlauf. */
export function ohneZitat(text: string): string {
  let ende = text.length;
  for (const re of ZITAT_BEGINN) {
    const m = re.exec(text);
    if (m && m.index < ende) ende = m.index;
  }
  return text.slice(0, ende);
}

const p2 = (n: number) => String(n).padStart(2, '0');

function gueltig(j: number, m: number, t: number): boolean {
  if (m < 1 || m > 12 || t < 1 || t > 31) return false;
  const d = new Date(j, m - 1, t);
  return d.getFullYear() === j && d.getMonth() === m - 1 && d.getDate() === t;
}

const MONATS_MUSTER = Object.keys(MONATE).sort((a, b) => b.length - a.length).join('|');
// 12.11.2026 · 12.11.26 · 12.11. · 5. November (2026)
const DATUM_RE = new RegExp(
  `(?<![\\d.])(\\d{1,2})\\.\\s?(?:(\\d{1,2})\\.(\\d{4}|\\d{2}(?!\\d))?|(${MONATS_MUSTER})\\.?(?:\\s+(\\d{4}))?)(?![\\d])`,
  'gi',
);
// direkt danach: „um 14:30 Uhr“, „, 14 Uhr“, „ab 9.30 Uhr“, „14:30“, „zwischen 14-18 Uhr“,
// „am Nachmittag, zwischen 15.00 und 16.00 Uhr“ (bis zu drei Wörter dazwischen)
const ZEIT_RE = /^[\s,]*(?:[A-Za-zäöüß().]{1,12}[\s,]+){0,3}?(\d{1,2})(?:[:.](\d{2}))?(?:\s*(?:-|–|bis|und)\s*(\d{1,2})(?:[:.](\d{2}))?)?\s*(Uhr|h\b)?/i;

// Ohne Jahr und schon vorbei: nur bei weit Zurückliegendem („10.01.“ im Dezember) ins nächste Jahr –
// „Ihre Mail vom 15. Oktober“ ist ein Rückbezug, kein Termin.
const TAGE_FUER_JAHRESWECHSEL = 200;

export interface TerminFund { datum: string; zeit: string | null; ende?: string | null }

const uhr = (h: string | undefined, m: string | undefined): string | null => {
  if (h === undefined) return null;
  const hh = Number(h);
  const mm = m ? Number(m) : 0;
  return hh <= 23 && mm <= 59 ? `${p2(hh)}:${p2(mm)}` : null;
};

/**
 * Erstes künftiges Datum im eigenen Mailtext (heute zählt). Ein Datum mit Uhrzeit hat Vorrang
 * vor einem ohne. Ohne Jahr: dieses Jahr, liegt es schon zurück, das nächste.
 */
export function findeTerminImText(text: string, jetzt: Date): TerminFund | null {
  const eigener = ohneZitat(text || '');
  const heute = `${jetzt.getFullYear()}-${p2(jetzt.getMonth() + 1)}-${p2(jetzt.getDate())}`;
  let ohneZeit: TerminFund | null = null;
  for (const m of eigener.matchAll(DATUM_RE)) {
    const tag = Number(m[1]);
    const monat = m[2] ? Number(m[2]) : MONATE[(m[4] || '').toLowerCase()];
    if (!monat) continue;
    const jahrText = m[3] ?? m[5];
    let jahr = jahrText ? Number(jahrText.length === 2 ? `20${jahrText}` : jahrText) : jetzt.getFullYear();
    if (!gueltig(jahr, monat, tag)) continue;
    let datum = `${jahr}-${p2(monat)}-${p2(tag)}`;
    if (!jahrText && datum < heute) {
      const zurueck = (new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate()).getTime() - new Date(jahr, monat - 1, tag).getTime()) / 86_400_000;
      if (zurueck < TAGE_FUER_JAHRESWECHSEL) continue;
      jahr += 1;
      if (!gueltig(jahr, monat, tag)) continue;
      datum = `${jahr}-${p2(monat)}-${p2(tag)}`;
    }
    if (datum < heute) continue;

    const rest = eigener.slice((m.index ?? 0) + m[0].length, (m.index ?? 0) + m[0].length + 60);
    const z = ZEIT_RE.exec(rest);
    // Ohne „Uhr“ nur mit Minuten als Uhrzeit werten („14:30“), sonst wäre jede Zahl eine Zeit.
    const zeit = z && (z[5] || z[2]) ? uhr(z[1], z[2]) : null;
    if (zeit) {
      const ende = z?.[3] ? uhr(z[3], z[4]) : null;
      return { datum, zeit, ende: ende && ende > zeit ? ende : null };
    }
    if (!ohneZeit) ohneZeit = { datum, zeit: null };
  }
  return ohneZeit;
}

/** Betreff ohne „AW:“, „RE:“, „WG:“, „FW:“ … am Anfang. */
export function betreffOhnePraefix(betreff: string | null | undefined): string {
  return (betreff || '').replace(/^\s*((AW|RE|WG|FW|FWD|Antwort|Weitergeleitet)\s*:\s*)+/i, '').trim();
}

export interface TerminAusMail {
  titel: string;
  beschreibung: string;
  /** 'YYYY-MM-DD' oder 'YYYY-MM-DDTHH:MM' (Formular-Vorbelegung); null = heute */
  start: string | null;
  /** 'YYYY-MM-DDTHH:MM' bei Zeitfenstern („14–18 Uhr“), sonst null */
  ende: string | null;
  objektId: string | null;
  mieterId: string | null;
  eigentuemerId: string | null;
}

const DATUM_LANG = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${p2(d.getDate())}.${p2(d.getMonth() + 1)}.${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

/** Vorbelegung fürs Terminformular aus einer Mail (Betreff, Absender, eigener Text, Zuordnungen). */
export function terminAusMail(
  email: { betreff?: string | null; von?: string | null; von_name?: string | null; datum?: string | null; objekt_id?: string | null; mieter_id?: string | null; eigentuemer_id?: string | null },
  text: string,
  jetzt: Date,
): TerminAusMail {
  const fund = findeTerminImText(text, jetzt);
  const absender = email.von_name && email.von && email.von_name !== email.von ? `${email.von_name} <${email.von}>` : (email.von || email.von_name || '');
  const auszug = ohneZitat(text || '').replace(/\n{3,}/g, '\n\n').trim();
  const kurz = auszug.length > 1200 ? `${auszug.slice(0, 1200).trimEnd()} …` : auszug;
  const kopf = ['Aus E-Mail', absender ? `von ${absender}` : '', email.datum ? `vom ${DATUM_LANG(email.datum)}` : ''].filter(Boolean).join(' ');
  const betreff = (email.betreff || '').trim();
  return {
    titel: betreffOhnePraefix(betreff) || 'Termin',
    beschreibung: [kopf + (betreff ? `\nBetreff: ${betreff}` : ''), kurz].filter(Boolean).join('\n\n'),
    start: fund ? (fund.zeit ? `${fund.datum}T${fund.zeit}` : fund.datum) : null,
    ende: fund?.ende ? `${fund.datum}T${fund.ende}` : null,
    objektId: email.objekt_id ?? null,
    mieterId: email.mieter_id ?? null,
    eigentuemerId: email.eigentuemer_id ?? null,
  };
}
